import {
    type ReactElement,
    type ReactNode,
    type Ref,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import type {
    ExpandedState,
    OnChangeFn,
    RowSelectionState,
} from "@tanstack/react-table";
import { parseISO } from "date-fns";
import type {
    ClientsWorkOverviewSortValue,
    ClientsWorkOverviewView,
} from "@common/types/views/clientsWorkOverviewView";
import type { View } from "@common/types/views/views";
import { useCurrentOrganizationMembers } from "@api/organizationMember.api";
import { useCurrentHour } from "@components/activityTimeline/useToday";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import {
    DataGrid,
    type DataGridHandle,
    type DataGridRow,
    type DataGridVirtualizationOptions,
    useDataGrid,
    useSavedViewState,
} from "@design-system/DataGrid";
import { useDynamicTopOffset } from "@design-system/hooks/useDynamicTopOffset";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import { useUserStatus } from "@hooks/useUserStatus";
import { styleWithVars } from "@lib/cssVars";
import type {
    ClientsWorkOverviewRow,
    ClientsWorkOverviewWorkRow,
} from "../clientsWorkOverviewViewConfig";
import { getWorkRows } from "../board/workBoardColumns";
import type { GroupBy } from "../useGroupedTableData";
import type { ClientsWorkOverviewCallbacks } from "../WorkRowActions";
import { WorkListActionsProvider } from "./WorkListActionsContext";
import { buildWorkListColumns, getWorkListSubRows } from "./workListColumns";
import { useWorkListEditing, WORK_LIST_EDITING } from "./workListEditing";
import { useWorkListServerState } from "./useWorkListServerState";
import { useWorkListShortcuts } from "./useWorkListShortcuts";
import styles from "./WorkListTable.module.scss";

export interface WorkListTableProps {
    /** `null` while the page loads. */
    rows: ClientsWorkOverviewRow[] | null;
    /** `rows` are the previous query's while the next one loads. */
    isRefetching: boolean;
    /** The page failed to load: the error state with Retry replaces `rows`. */
    isError: boolean;
    onRetry: () => void;
    expanded: ExpandedState;
    onExpandedChange: (expanded: ExpandedState) => void;
    /** The page's resolved grouping; `rows` are already grouped by it. */
    groupBy: GroupBy;
    view: ClientsWorkOverviewView;
    /** The view column settings compare against; hidden columns keep its order. */
    defaultView: View;
    onViewChange: (view: ClientsWorkOverviewView) => void;
    sort: ClientsWorkOverviewSortValue;
    onSortChange: (sort: ClientsWorkOverviewSortValue) => void;
    /** 1-based, as the server pages clients. */
    page: number;
    pageCount: number;
    onPageChange: (page: number) => void;
    rowSelection: RowSelectionState;
    onRowSelectionChange: OnChangeFn<RowSelectionState>;
    /** x on a row: work rows toggle with their copies, other rows are ignored. */
    onToggleListRow: (rowId: string) => void;
    /** Shift+Arrow from outside the grid: selects a work row with its copies, other rows are ignored. */
    onSelectListRow: (rowId: string) => void;
    /** Shift+A: toggles every work row. */
    onToggleAllListRows: () => void;
    /** The work item open in the detail pane: each of its rows is drawn active. */
    activeRow: ClientsWorkOverviewWorkRow | null;
    onOpenWorkItem: (row: ClientsWorkOverviewWorkRow) => void;
    /** The row actions menu's callbacks; its Open goes through the table's own open. */
    callbacks: ClientsWorkOverviewCallbacks;
    emptyView: ReactNode;
    footer: ReactNode;
    ref?: Ref<DataGridHandle>;
    virtualization?: DataGridVirtualizationOptions;
}

const getRowId = (row: ClientsWorkOverviewRow) => row.id;

const isSelectableWorkRow = (row: ClientsWorkOverviewRow) =>
    row.rowType === "work" && !isBusyClosesDemoId(row.closeId);

/** Every copy of an engagement shares its close id, so all of them draw active. */
const getRowActiveKey = (row: ClientsWorkOverviewRow) =>
    row.rowType === "work" ? row.closeId : null;

/** The open engagement's row: the person-group copy that was opened, else its first copy (opened from a link). */
const findActiveRowId = ({
    rows,
    closeId,
    openedRowId,
}: {
    rows: readonly ClientsWorkOverviewRow[];
    closeId: string;
    openedRowId: string | null;
}) => {
    const copies = getWorkRows(rows).filter((row) => row.closeId === closeId);
    return (copies.find((row) => row.id === openedRowId) ?? copies[0])?.id;
};

/**
 * The Work page's List on DataGrid: clients or groups with their work nested,
 * sorted and paged by the server, with the saved view's columns. Clicking or
 * pressing Enter on work opens its details; on a client or group it expands.
 */
export const WorkListTable = ({
    rows,
    isRefetching,
    isError,
    onRetry,
    expanded,
    onExpandedChange,
    groupBy,
    view,
    defaultView,
    onViewChange,
    sort,
    onSortChange,
    page,
    pageCount,
    onPageChange,
    rowSelection,
    onRowSelectionChange,
    onToggleListRow,
    onSelectListRow,
    onToggleAllListRows,
    activeRow,
    onOpenWorkItem,
    callbacks,
    emptyView,
    footer,
    ref,
    virtualization,
}: Readonly<WorkListTableProps>): ReactElement => {
    const gridRef = useRef<DataGridHandle>(null);
    const handleRef = mergeRefs([gridRef, ref]);
    const shortcuts = useWorkListShortcuts({
        gridRef,
        onToggleListRow,
        onSelectListRow,
        onToggleAllListRows,
    });
    const containerRef = useRef<HTMLDivElement>(null);
    const { topOffset } = useDynamicTopOffset({ containerRef });
    const activeCloseId = activeRow?.closeId ?? null;
    // Autofocus scrolls to the first row: once an item has been open, closing it must not jump the list.
    const [hasOpenedItem, setHasOpenedItem] = useState(activeRow !== null);
    if (activeRow && !hasOpenedItem) {
        setHasOpenedItem(true);
    }
    const [openedRowId, setOpenedRowId] = useState<string | null>(null);

    const { data: members } = useCurrentOrganizationMembers();
    const userId = useUserStatus().data?.user.id;
    const { editing, confirmationModal } = useWorkListEditing();
    const currentHour = useCurrentHour();
    const columns = useMemo(
        () =>
            buildWorkListColumns({
                members,
                userId,
                editing,
                now: parseISO(currentHour),
            }),
        [members, userId, editing, currentHour],
    );
    const savedViewState = useSavedViewState({
        view,
        onViewChange,
        columns,
        defaultView,
    });
    const serverState = useWorkListServerState({
        rows,
        isRefetching,
        isError,
        onRetry,
        expanded,
        onExpandedChange,
        sort,
        onSortChange,
        page,
        onPageChange,
    });

    const openWorkRow = useCallback(
        (row: ClientsWorkOverviewWorkRow) => {
            setOpenedRowId(row.id);
            onOpenWorkItem(row);
        },
        [onOpenWorkItem],
    );
    const handleRowOpen = useCallback(
        (row: DataGridRow<ClientsWorkOverviewRow>) =>
            row.original.rowType === "work"
                ? openWorkRow(row.original)
                : row.toggleExpanded(),
        [openWorkRow],
    );

    const table = useDataGrid({
        data: rows ?? undefined,
        columns,
        getRowId,
        status: serverState.status,
        noun: "work items",
        expanding: {
            state: expanded,
            onChange: serverState.handleExpandedChange,
            getSubRows: getWorkListSubRows,
            parentRows: "summary",
        },
        sorting: {
            mode: "server",
            state: serverState.sorting,
            onChange: serverState.handleSortingChange,
        },
        pagination: {
            mode: "server",
            pageCount,
            state: serverState.pagination,
            onChange: serverState.handlePaginationChange,
        },
        rowSelection: {
            state: rowSelection,
            onChange: onRowSelectionChange,
            isRowSelectable: isSelectableWorkRow,
            subRows: "cascade",
        },
        listEditing: WORK_LIST_EDITING,
        getRowActiveKey,
        onRowOpen: handleRowOpen,
        virtualization,
        ...savedViewState,
    });

    const activeRowId = useMemo(
        () =>
            activeCloseId && rows
                ? findActiveRowId({ rows, closeId: activeCloseId, openedRowId })
                : undefined,
        [rows, activeCloseId, openedRowId],
    );
    const { activeRowKey } = table.atoms;
    useEffect(() => {
        activeRowKey.set(activeCloseId);
        if (activeRowId) {
            gridRef.current?.scrollToRow(activeRowId);
        }
    }, [activeCloseId, activeRowId, activeRowKey, gridRef]);

    return (
        <div
            ref={containerRef}
            className={styles.root}
            data-grouping={groupBy}
            style={styleWithVars({
                "--work-list-top-offset": `${topOffset}px`,
            })}
            onPointerEnter={shortcuts.activate}
            onFocus={shortcuts.activate}
        >
            <WorkListActionsProvider
                callbacks={callbacks}
                onOpenDetails={openWorkRow}
            >
                <DataGrid
                    ref={handleRef}
                    table={table}
                    autoFocus={!hasOpenedItem}
                    aria-label="Work"
                    className={styles.table}
                >
                    <DataGrid.Grid />
                    <DataGrid.Empty>{emptyView}</DataGrid.Empty>
                    <DataGrid.Error />
                    <div className={styles.footer}>{footer}</div>
                </DataGrid>
            </WorkListActionsProvider>
            {confirmationModal}
        </div>
    );
};
